import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PawktLogo } from "@/components/pawkt/PawktLogo";
import { NAV, NAV_GROUPS, applyRoleNav, navPermOk } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";
import { hasPerm, moduleOn } from "@/lib/perms";
import { cn } from "@/lib/utils";
import { SidebarSearchInput, SidebarSearchResults, searchNav } from "./SidebarSearch";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// Longest matching route wins (e.g. /communications/templates over /communications).
const activeItemFor = (items, path) =>
  items.filter((i) => path === i.to || path.startsWith(i.to + "/")).sort((a, b) => b.to.length - a.to.length)[0];

const readPrefs = (key) => {
  if (!key) return {};
  try { return JSON.parse(localStorage.getItem(key)) || {}; } catch { return {}; }
};

function useGroupPrefs(key, activeGroup) {
  const [state, setState] = useState(() => ({ key, open: readPrefs(key) }));
  const open = state.key === key ? state.open : readPrefs(key);
  const save = (next) => {
    setState({ key, open: next });
    if (key) { try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* storage unavailable */ } }
  };
  useEffect(() => {
    if (activeGroup && !open[activeGroup]) save({ ...open, [activeGroup]: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, activeGroup]);
  return [open, (g) => save({ ...open, [g]: !open[g] })];
}

function useLeadUnread(enabled) {
  const q = useQuery({ queryKey: ["leads-unread"], queryFn: async () => (await api.get("/leads/unread-count")).data, enabled, refetchInterval: 60000, refetchOnWindowFocus: true, staleTime: 30000 });
  return enabled ? q.data?.count || 0 : 0;
}

function LeadBadge({ count }) {
  if (!count) return null;
  return (
    <span className="ml-auto shrink-0 min-w-[20px] rounded-full bg-pawkt-primary-soft px-1.5 py-0.5 text-center text-[11px] font-bold leading-none text-pawkt-primary-deep tabular-nums" aria-label={`${count} new leads`} data-testid="nav-leads-badge">
      {count > 99 ? "99+" : count}
    </span>
  );
}

function NavItem({ item, active, nested, onNavigate, badge }) {
  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      data-testid={`nav-${item.label.toLowerCase()}`}
      aria-current={active ? "page" : undefined}
      className={cn(
        "group relative flex items-center gap-3 rounded-xl px-3 text-sm font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60",
        nested ? "py-2 pl-9" : "py-2.5",
        active ? "bg-pawkt-primary-soft text-pawkt-primary-deep" : "text-pawkt-text-secondary hover:bg-pawkt-elevated hover:text-pawkt-text"
      )}
    >
      <span className={cn("absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-full bg-primary transition-opacity", active ? "opacity-100" : "opacity-0")} />
      <item.icon className={cn("shrink-0", nested ? "h-[18px] w-[18px]" : "h-[20px] w-[20px]")} />
      <span className="min-w-0 truncate">{item.label}</span>
      {item.to === "/leads" && <LeadBadge count={badge} />}
    </Link>
  );
}

function NavGroup({ group, items, open, isActiveGroup, activeTo, onToggle, onNavigate, badge }) {
  const id = `nav-group-panel-${group.key}`;
  return (
    <div data-testid={`nav-group-${slug(group.label)}`} className="pt-1">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={id}
        title={group.label}
        data-testid={`nav-group-toggle-${group.key}`}
        className={cn(
          "flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-bold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60",
          isActiveGroup ? "text-pawkt-primary-deep" : "text-pawkt-text hover:bg-pawkt-elevated"
        )}
      >
        <group.icon className="h-[20px] w-[20px] shrink-0" />
        <span className="flex-1 truncate">{group.label}</span>
        <ChevronDown className={cn("h-4 w-4 shrink-0 transition-transform duration-200", open ? "rotate-0" : "-rotate-90")} aria-hidden="true" />
      </button>
      {open && (
        <div id={id} role="group" aria-label={group.label} className="mt-0.5 space-y-0.5">
          {items.map((item) => (
            <NavItem key={item.to} item={item} nested active={item.to === activeTo} onNavigate={onNavigate} badge={badge} />
          ))}
        </div>
      )}
    </div>
  );
}

export function Sidebar({ onNavigate }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const sflags = user?.server_flags || {};
  const bizAdmin = ["owner", "admin"].includes(user?.role) && !user?.ca_ctx;
  const { data: caMe } = useQuery({ queryKey: ["ca-me"], queryFn: async () => (await api.get("/ca/me")).data, enabled: bizAdmin, staleTime: 300000, retry: false });
  // CA Firm Workspace link: hidden for business Owner/Admin unless they belong to a CA firm
  const caHidden = bizAdmin && !caMe?.membership;
  const items = applyRoleNav(NAV.filter((n) => (!n.flag || moduleOn(user, n.flag)) && navPermOk(user, n) && (!n.serverFlag || sflags[n.serverFlag]) && !(n.to === "/ca" && caHidden)), user);
  const activeItem = activeItemFor(items, pathname.startsWith("/settings/ai-receptionist") ? "/shally" : pathname);
  const leadUnread = useLeadUnread(items.some((i) => i.to === "/leads") && hasPerm(user, "leads.view"));
  const tenantId = user?.tenant_id || user?.tenant?.id;
  const prefsKey = user?.id && tenantId ? `pawkt.sidebar.groups.${user.id}.${tenantId}` : null;
  const [open, toggle] = useGroupPrefs(prefsKey, activeItem?.group === "dashboard" ? null : activeItem?.group);
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [hl, setHl] = useState(0);
  const searching = !!query.trim();
  const results = searching ? searchNav(items, query) : [];
  const flat = results.flatMap((g) => g.items);
  const hlItem = flat[Math.min(hl, flat.length - 1)];

  const changeQuery = (v) => { setQuery(v); setHl(0); };
  const select = (item, go) => {
    if (go) navigate(item.to);
    changeQuery("");
    onNavigate?.();
  };
  const onSearchKey = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      if (query) changeQuery(""); else e.currentTarget.blur();
    } else if (!flat.length) {
      return;
    } else if (e.key === "ArrowDown") {
      e.preventDefault(); setHl((i) => (i + 1) % flat.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault(); setHl((i) => (i - 1 + flat.length) % flat.length);
    } else if (e.key === "Enter" && hlItem) {
      e.preventDefault(); select(hlItem, true);
    }
  };

  const groups = useMemo(
    () => NAV_GROUPS.map((g) => ({ group: g, items: items.filter((i) => i.group === g.key) })).filter((g) => g.items.length),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items.map((i) => i.to).join("|")]
  );

  return (
    <aside className="flex h-full w-[260px] flex-col border-r border-pawkt-border bg-white" data-testid="sidebar">
      <div className="flex items-center px-5 h-16 border-b border-pawkt-border-soft">
        <PawktLogo variant="sidebar" subtitle="Pet Business OS" />
      </div>

      <SidebarSearchInput query={query} onChange={changeQuery} onKeyDown={onSearchKey} activeItem={searching ? hlItem : null} hasResults={flat.length > 0} />

      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5" aria-label="Main navigation">
        {searching ? (
          <SidebarSearchResults query={query} groups={results} activeItem={hlItem} onHover={(it) => setHl(flat.indexOf(it))} onSelect={select} />
        ) : groups.map(({ group, items: gi }) =>
          group.standalone ? (
            gi.map((item) => <NavItem key={item.to} item={item} active={item.to === activeItem?.to} onNavigate={onNavigate} badge={leadUnread} />)
          ) : (
            <NavGroup
              key={group.key}
              group={group}
              items={gi}
              open={!!open[group.key]}
              isActiveGroup={activeItem?.group === group.key}
              activeTo={activeItem?.to}
              onToggle={() => toggle(group.key)}
              onNavigate={onNavigate}
              badge={leadUnread}
            />
          )
        )}
      </nav>

      <div className="border-t border-pawkt-border-soft px-4 py-3">
        <div className="text-xs text-pawkt-text-disabled">Plan</div>
        <div className="text-sm font-bold text-pawkt-text capitalize">{user?.tenant?.subscription_plan || "—"}</div>
      </div>
    </aside>
  );
}
